import {
  ADMIN_ROLE,
  CARER_ROLE,
  COORDINATOR_ROLE,
  CUSTOMER_FAMILY_REPRESENTATIVE_ROLE,
  CUSTOMER_MEMBER_ROLE,
  CUSTOMER_SELF_ROLE,
  DASHBOARD_ROLES,
  FAMILY_ROLE,
  highestRole,
  ORGANISATION_ADMIN_ROLE,
  SMALL_LOGO,
} from '@careapp-group/core';

import { createSelector } from '@ngrx/store';
import { auth } from '../auth/auth.selectors';
import {
  CUSTOMERS,
  getEntities,
  getOrganisationState,
  getSelected,
  getTenantsState,
  org,
} from '../reducers';
import {
  CustomerDto,
  OrganisationConfigDto,
  OrganisationDto,
  OrganisationPreferenceDto,
  PermissionDto,
} from '@careapp-group/ng-sdk';

export const getUserOrganisationRoles = org?.currentOrgRoles;

export const getOrganisationConfig = org?.currentOrgConfiguration;

export const getOrganisationPreferences = org?.currentOrgPreferences;

// Retrieve the ID of the tenant owning the currently selected organisation.
export const getCurrentOrganisationTenantId = createSelector(
  getOrganisationState,
  (orgState): string => {
    if (
      !!orgState &&
      orgState.selectedId != null &&
      orgState.entities != null &&
      orgState.entities[orgState.selectedId] != null
    ) {
      return orgState?.entities[orgState.selectedId]?.tenantId;
    }
  }
);

export const getCurrentOrganisationId = createSelector(
  getOrganisationState,
  (orgState): string => orgState.selectedId
);

export const getEnabledOrganisationsUnderCurrentTenant = createSelector(
  getOrganisationState,
  getTenantsState,
  (orgState, tenantState): { [key: string]: OrganisationDto } => {
    const currentTenantId =
      tenantState.selectedId ?? orgState?.entities[orgState.selectedId]?.tenantId;

    if (!currentTenantId) {
      return { [orgState.selectedId]: orgState.entities[orgState.selectedId] };
    }

    return orgState.ids
      .filter(
        (orgId) =>
          orgState.entities[orgId].tenantId === currentTenantId && orgState.entities[orgId].enabled
      )
      .reduce((acc, orgId) => ({ ...acc, [orgId]: orgState.entities[orgId] }), {});
  }
);

export const highestPermissionForCurrentOrganisation = createSelector(
  org.currentOrgPermissions,
  (permissions: PermissionDto[]) => {
    const highestRoleName = highestRole(permissions.map((permission) => permission.role));
    if (!highestRoleName) {
      return;
    }
    return permissions.find((permission) => permission.role === highestRoleName);
  }
);

export const canAccessAllCustomers = createSelector(
  highestPermissionForCurrentOrganisation,
  (permission: PermissionDto) =>
    permission
      ? !!(
          DASHBOARD_ROLES.includes(permission.role) ||
          !!(permission.qualifications || {})['accessAllCustomers']
        )
      : false
);

export const getIsCoordinatorAndDoesNotOverseeAll = createSelector(
  highestPermissionForCurrentOrganisation,
  auth.getRoles,
  getUserOrganisationRoles ?? null,
  (permission: PermissionDto, authRoles: string[], orgRoles: string[]) => {
    const accessAllCustomers = permission?.qualifications?.accessAllCustomers;
    if (authRoles && orgRoles && accessAllCustomers === false) {
      const allRoles = [...(authRoles ?? []), ...(orgRoles ?? [])];
      return allRoles.some((role) => role === COORDINATOR_ROLE) ?? false;
    }
    return false;
  }
);

export const getIsFamilyAndCanCreateCheckins = createSelector(
  getOrganisationPreferences,
  getUserOrganisationRoles ?? null,
  (organisationPreferences?: OrganisationPreferenceDto, orgRoles?: string[]) => {
    if (!getOrganisationPreferences || !getUserOrganisationRoles) {
      return false;
    }

    const isFamily = orgRoles.includes(FAMILY_ROLE);
    const canPostContent = organisationPreferences?.familyCanPostContent === true;
    const canSeeCheckins =
      organisationPreferences?.minCheckInShareScore > -1 &&
      organisationPreferences?.maxCheckInShareScore > -1;

    return isFamily && canPostContent && canSeeCheckins;
  }
);

export const getIsFamily = createSelector(
  getUserOrganisationRoles ?? null,
  (orgRoles: string[]) => {
    return (orgRoles ?? []).some((role) => role === FAMILY_ROLE) ?? false;
  }
);

export const getAccessAllCustomers = createSelector(
  highestPermissionForCurrentOrganisation,
  (permission: PermissionDto) => permission?.qualifications?.accessAllCustomers ?? null
);

export const getCurrentOrgAppBarLogo = createSelector(org.currentOrgBranding, (branding) =>
  branding?.media?.find((media) => media.purpose === SMALL_LOGO)
);

export const canShareToFamily = createSelector(
  auth.getRoles,
  getUserOrganisationRoles ?? null,
  (authRoles: string[], orgRoles: string[]) => {
    if (authRoles && orgRoles) {
      const shareToFamilyRoles = [...DASHBOARD_ROLES, CUSTOMER_MEMBER_ROLE];
      const allRoles = [...(authRoles ?? []), ...(orgRoles ?? [])];
      return allRoles.some((role) => shareToFamilyRoles.includes(role)) ?? false;
    }
    return false;
  }
);
// Checks if the organisation allows this user to share to public.
// Still requires checking all customers and the memo itself.
export const canShareToPublic = createSelector(
  getOrganisationPreferences,
  getUserOrganisationRoles,
  getOrganisationConfig,
  auth.getRoles,
  (
    organisationPreferences: OrganisationPreferenceDto,
    organisationRoles: string[],
    organisationConfig: OrganisationConfigDto,
    authRoles: string[]
  ) => {
    const shareToPublicRoles = [...(organisationPreferences?.shareToPublicRoles ?? []), ADMIN_ROLE];
    const orgPublicPostingAllowed = organisationConfig?.publicPosting ?? false;
    const allUserRoles = [...(organisationRoles ?? []), ...(authRoles ?? [])];
    // Public posting must be enabled on an organisation level.
    if (!(orgPublicPostingAllowed ?? false)) {
      return false;
    }
    // The user's org roles must overlap with roles that are allowed by the org to publicly share content
    if (
      (shareToPublicRoles?.length === 0 ||
        !shareToPublicRoles.some((role) => allUserRoles.includes(role))) ??
      true
    ) {
      return false;
    }
    return true;
  }
);

export const orgPreferencesForSelectedCustomer = createSelector(
  getSelected(CUSTOMERS),
  getOrganisationState,
  (customer: CustomerDto, orgState) => {
    if (customer) {
      return orgState?.preferences[customer.organisationId];
    }
  }
);

export const canFamilyRepsPostCheckIns = createSelector(
  getOrganisationPreferences,
  (preferences) => {
    if (!preferences) {
      return false;
    }
    return (
      preferences?.familyCanPostContent == true &&
      preferences?.minCheckInShareScore != null &&
      preferences!.minCheckInShareScore! > -1 &&
      preferences?.maxCheckInShareScore != null &&
      preferences!.maxCheckInShareScore! > -1
    );
  }
);

export const canFamilyPostContent = createSelector(getOrganisationPreferences, (preferences) => {
  if (!preferences) {
    return false;
  }
  return preferences?.familyCanPostContent == true;
});

export const canUserPostCheckins = createSelector(
  canFamilyRepsPostCheckIns,
  getUserOrganisationRoles,
  getEntities(CUSTOMERS),
  getSelected(CUSTOMERS),
  (
    canFamilyRepsPost,
    orgRoles,
    customers: { [id: string]: CustomerDto },
    selectedCustomer: CustomerDto
  ) => {
    const allowedOrgRoles = [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE, CARER_ROLE];
    const allowedCustomerRoles = [CUSTOMER_FAMILY_REPRESENTATIVE_ROLE, CUSTOMER_SELF_ROLE];

    if (orgRoles.some((role) => allowedOrgRoles.includes(role)) ?? false) {
      return true;
    } else if (selectedCustomer != null) {
      return (
        customers[selectedCustomer.id]?.permissions?.some((p) =>
          allowedCustomerRoles.includes(p.role)
        ) ?? false
      );
    } else if (
      canFamilyRepsPost &&
      Object.values(customers).some(
        (c) => c.permissions?.some((p) => allowedCustomerRoles.includes(p.role)) ?? false
      )
    ) {
      return true;
    }
    return false;
  }
);

export const canUserPostDocsAndLinks = createSelector(
  canFamilyPostContent,
  getUserOrganisationRoles,
  getEntities(CUSTOMERS),
  getSelected(CUSTOMERS),
  (
    canFamilyRepsPost,
    orgRoles,
    customers: { [id: string]: CustomerDto },
    selectedCustomer: CustomerDto
  ) => {
    const allowedOrgRoles = [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE];
    const allowedCustomerRoles = [CUSTOMER_FAMILY_REPRESENTATIVE_ROLE, CUSTOMER_SELF_ROLE];

    if (orgRoles.some((role) => allowedOrgRoles.includes(role)) ?? false) {
      return true;
    } else if (selectedCustomer != null) {
      return (
        customers[selectedCustomer.id]?.permissions?.some((p) =>
          allowedCustomerRoles.includes(p.role)
        ) ?? false
      );
    } else if (
      canFamilyRepsPost &&
      Object.values(customers).some(
        (c) => c.permissions?.some((p) => allowedCustomerRoles.includes(p.role)) ?? false
      )
    ) {
      return true;
    }
    return false;
  }
);
